import { useEffect } from 'react';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { getNextPageParam, getPagesContent, useAppSelector } from '@pkg/hooks';

import { callGetTeams, COMPETITION_TEAMS_CACHE } from '@app/client/resources/competitions';
import { useFantasyTeamsStore } from '@app/store/zustand';

const cache = [COMPETITION_TEAMS_CACHE];

export function useGetUserTeams(disabled?: boolean, ignoreSignal?: boolean) {
    const userId = useAppSelector((s) => s.user.userAccount?.user.id);
    const setUserTeam = useFantasyTeamsStore((state) => state.setUserTeam);
    const userTeam = useFantasyTeamsStore((state) => state.userTeam);

    const { data, ...rest } = useInfiniteQuery(
        cache,
        ({ pageParam = 0, signal }) =>
            callGetTeams({ settings: { signal: ignoreSignal ? undefined : signal }, query: { page: pageParam } }),
        { getNextPageParam, keepPreviousData: true, retry: false, enabled: !!userId && !disabled && !userTeam },
    );

    const teamItems = getPagesContent(data?.pages);
    const team = teamItems?.[0];

    useEffect(() => {
        if (team && (!userTeam || userTeam?.id !== team?.id)) {
            const isOwner = !!team?.membership?.find((m) => m.user_id === userId && m.role === 'Admin');
            setUserTeam({ ...team, isOwner });
        }
    }, [setUserTeam, team, userId, userTeam]);

    return { data, team, ...rest };
}

export function useGetUserTeamsAsync() {
    const queryClient = useQueryClient();
    return () => queryClient.ensureQueryData(cache, () => callGetTeams({ query: { page: 0 } }));
}
